import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

interface Employee {
  id: number
  fullName: string
  folderName: string
  marca: string | null
  eaAccountCode: string
  eaAccountCodeEur: string | null
  serieDecont: string | null
  defaultCostCenterId: number | null
  defaultAvans: string
  active: boolean
  userId: number | null
  defaultCostCenter: { id: number; code: string; name: string } | null
  user: { id: number; name: string; email: string } | null
  _count: { deconturi: number }
}

interface CostCenter {
  id: number
  code: string
  name: string
}

interface CrmUser {
  id: number
  name: string
  email: string
}

const Schema = z.object({
  fullName: z.string().min(1, 'Nume obligatoriu'),
  folderName: z.string().min(1, 'Folder Nextcloud obligatoriu'),
  marca: z.string().optional(),
  eaAccountCode: z.string().min(1, 'Cont EA obligatoriu'),
  eaAccountCodeEur: z.string().optional(),
  serieDecont: z.string().max(10, 'Maxim 10 caractere').optional(),
  defaultCostCenterId: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number().int().positive().optional(),
  ),
  defaultAvans: z.coerce.number().nonnegative(),
  active: z.boolean().default(true),
  userId: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number().int().positive().optional(),
  ),
})

type Input = z.infer<typeof Schema>

export function SetariAngajatiPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('hr.employees:write')
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Employee | null>(null)

  const query = useQuery<{ employees: Employee[] }>({
    queryKey: ['employees'],
    queryFn: async () => (await api.get<{ employees: Employee[] }>('/employees')).data,
  })

  const ccQuery = useQuery<{ items: CostCenter[] }>({
    queryKey: ['cost-centers'],
    queryFn: async () => (await api.get<{ items: CostCenter[] }>('/settings/cost-centers')).data,
  })

  const usersQuery = useQuery<{ users: CrmUser[] }>({
    queryKey: ['crm-users'],
    queryFn: async () => (await api.get<{ users: CrmUser[] }>('/settings/users')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Input) => (await api.post('/employees', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      toast.success('Angajat adăugat')
      setCreateOpen(false)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Input }) =>
      (await api.patch(`/employees/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      toast.success('Actualizat')
      setEditTarget(null)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/employees/${id}`)).data,
    onSuccess: (data: { deactivated?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      toast.success(data?.deactivated ? 'Angajat dezactivat (are deconturi)' : 'Șters')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const employees = useMemo(() => query.data?.employees ?? [], [query.data])
  const costCenters = useMemo(() => ccQuery.data?.items ?? [], [ccQuery.data])
  const crmUsers = useMemo(() => usersQuery.data?.users ?? [], [usersQuery.data])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Angajați</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Adaugă angajat</Button>}
      </div>

      <Card>
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : employees.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun angajat. Adaugă angajații care depun deconturi.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Nume</th>
                <th className="py-2 pr-3 font-medium">Folder Nextcloud</th>
                <th className="py-2 pr-3 font-medium">Cont EA (RON)</th>
                <th className="py-2 pr-3 font-medium">Cont EA (EUR)</th>
                <th className="py-2 pr-3 font-medium">Avans implicit</th>
                <th className="py-2 pr-3 font-medium">Deconturi</th>
                <th className="py-2 pr-3 font-medium">Stare</th>
                {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => {
                const eaWarn = !e.eaAccountCode.startsWith('542.')
                return (
                  <tr key={e.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-medium">
                      {e.fullName}
                      {e.user && (
                        <div className="text-[10px] font-normal text-b4l-text-muted">
                          cont: {e.user.name}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{e.folderName}</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      <span className={eaWarn ? 'text-amber-600' : ''}>{e.eaAccountCode}</span>
                      {eaWarn && <span title="Contul ar trebui să înceapă cu 542."> ⚠</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-b4l-text-muted">
                      {e.eaAccountCodeEur ?? '—'}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">
                      {Number(e.defaultAvans).toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-2 pr-3 text-center">{e._count.deconturi}</td>
                    <td className="py-2 pr-3">
                      {e.active ? (
                        <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] text-green-700">
                          activ
                        </span>
                      ) : (
                        <span className="rounded bg-b4l-bg px-2 py-0.5 text-[10px] text-b4l-text-muted">
                          inactiv
                        </span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(e)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (
                                confirm(
                                  `Ștergi angajatul ${e.fullName}? Dacă are deconturi în istoric, contul se dezactivează și rămâne în evidență; dacă nu are, se șterge definitiv.`,
                                )
                              )
                                deleteMutation.mutate(e.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <EmployeeFormModal
          title="Adaugă angajat"
          submitLabel="Creează"
          costCenters={costCenters}
          crmUsers={crmUsers}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <EmployeeFormModal
          title={`Editează ${editTarget.fullName}`}
          submitLabel="Salvează"
          costCenters={costCenters}
          crmUsers={crmUsers}
          initial={{
            fullName: editTarget.fullName,
            folderName: editTarget.folderName,
            marca: editTarget.marca ?? '',
            eaAccountCode: editTarget.eaAccountCode,
            eaAccountCodeEur: editTarget.eaAccountCodeEur ?? '',
            serieDecont: editTarget.serieDecont ?? '',
            defaultCostCenterId: editTarget.defaultCostCenterId ?? undefined,
            defaultAvans: Number(editTarget.defaultAvans),
            active: editTarget.active,
            userId: editTarget.userId ?? undefined,
          }}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

interface EmployeeFormModalProps {
  title: string
  submitLabel: string
  costCenters: CostCenter[]
  crmUsers: CrmUser[]
  initial?: Input
  onClose: () => void
  onSubmit: (data: Input) => void
  submitting: boolean
}

function EmployeeFormModal({
  title,
  submitLabel,
  costCenters,
  crmUsers,
  initial,
  onClose,
  onSubmit,
  submitting,
}: EmployeeFormModalProps) {
  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: initial ?? {
      fullName: '',
      folderName: '',
      marca: '',
      eaAccountCode: '542.',
      eaAccountCodeEur: '',
      serieDecont: '',
      defaultAvans: 1500,
      active: true,
    },
  })

  const eaValue = form.watch('eaAccountCode')
  const eaWarn = eaValue !== undefined && eaValue.length > 0 && !eaValue.startsWith('542.')

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => form.handleSubmit(onSubmit)()} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form className="space-y-3" noValidate onSubmit={(e) => e.preventDefault()}>
        <Field
          label="Nume complet"
          {...form.register('fullName')}
          error={form.formState.errors.fullName?.message}
        />
        <Field
          label="Folder Nextcloud"
          hint="EXACT numele folderului din /DECONTURI/{luna}/"
          {...form.register('folderName')}
          error={form.formState.errors.folderName?.message}
        />
        <Field
          label="Cont EA — RON"
          hint={
            eaWarn
              ? '⚠ Contul ar trebui să înceapă cu 542.'
              : 'Analiticul exact din EA, ex: 542.Doru Lacatusu'
          }
          {...form.register('eaAccountCode')}
          error={form.formState.errors.eaAccountCode?.message}
        />
        <Field
          label="Cont EA — EUR (opțional)"
          hint="Doar dacă angajatul are decont în EUR (ex: 542.Ciortan Victor EUR)"
          {...form.register('eaAccountCodeEur')}
          error={form.formState.errors.eaAccountCodeEur?.message}
        />
        <Field
          label="Serie decont (opțional)"
          hint="Apare în titlu: „Decont de cheltuieli nr GC 7”. Gol = inițialele numelui."
          {...form.register('serieDecont')}
          error={form.formState.errors.serieDecont?.message}
        />
        <div>
          <label className="mb-1 block text-sm font-medium">Centru de cost implicit</label>
          <select
            {...form.register('defaultCostCenterId')}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— niciunul —</option>
            {costCenters.map((cc) => (
              <option key={cc.id} value={cc.id}>
                {cc.code} — {cc.name}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Avans implicit (RON)"
          type="number"
          step="0.01"
          {...form.register('defaultAvans')}
          error={form.formState.errors.defaultAvans?.message}
        />
        <Field
          label="Marca / cod intern (opțional)"
          {...form.register('marca')}
          error={form.formState.errors.marca?.message}
        />
        <div>
          <label className="mb-1 block text-sm font-medium">Cont CRM asociat (opțional)</label>
          <select
            {...form.register('userId')}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— niciunul —</option>
            {crmUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-b4l-text-muted">
            Permite angajatului să încarce bonuri din pagina „Încarcă bon” cu contul lui.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...form.register('active')} />
          Activ
        </label>
      </form>
    </Modal>
  )
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  )
}
